<script setup>
import { ref, watch, onMounted, nextTick } from 'vue';

const props = defineProps({
  modelValue: { type: String, default: '' },
});

const emit = defineEmits(['update:modelValue']);

const editorRef = ref(null);
const activeTab = ref('visual'); // 'visual' or 'html'
const htmlSource = ref(props.modelValue);

const updateFromEditor = () => {
  if (editorRef.value) {
    const html = editorRef.value.innerHTML;
    htmlSource.value = html;
    emit('update:modelValue', html);
  }
};

const updateFromSource = () => {
  emit('update:modelValue', htmlSource.value);
  if (editorRef.value) {
    editorRef.value.innerHTML = htmlSource.value;
  }
};

const switchTab = tab => {
  if (tab === 'html') {
    if (editorRef.value) {
      htmlSource.value = editorRef.value.innerHTML;
    }
  } else if (tab === 'visual') {
    if (editorRef.value) {
      editorRef.value.innerHTML = htmlSource.value;
    }
  }
  activeTab.value = tab;
};

const execCmd = (command, value = null) => {
  editorRef.value?.focus();
  document.execCommand(command, false, value);
  updateFromEditor();
};

const formatBlock = tag => {
  execCmd('formatBlock', tag);
};

const insertLink = () => {
  const url = prompt('URL do link:');
  if (url) execCmd('createLink', url);
};

const setColor = event => {
  execCmd('foreColor', event.target.value);
};

const setBgColor = event => {
  execCmd('hiliteColor', event.target.value);
};

const insertVariable = variable => {
  execCmd('insertText', `{{${variable}}}`);
};

const insertImage = () => {
  const url = prompt('URL da imagem:');
  if (url) execCmd('insertImage', url);
};

watch(
  () => props.modelValue,
  newVal => {
    if (
      activeTab.value === 'visual' &&
      editorRef.value &&
      editorRef.value.innerHTML !== newVal
    ) {
      editorRef.value.innerHTML = newVal;
    }
    if (activeTab.value === 'html' && htmlSource.value !== newVal) {
      htmlSource.value = newVal;
    }
  }
);

onMounted(() => {
  nextTick(() => {
    if (editorRef.value && props.modelValue) {
      editorRef.value.innerHTML = props.modelValue;
    }
  });
});
</script>

<template>
  <div class="border rounded-lg border-n-strong overflow-hidden">
    <!-- Tabs -->
    <div class="flex border-b border-n-strong bg-n-surface-2">
      <button
        class="px-3 py-1.5 text-xs font-medium"
        :class="
          activeTab === 'visual'
            ? 'text-n-brand border-b-2 border-n-brand'
            : 'text-n-slate-10 hover:text-n-slate-12'
        "
        @click="switchTab('visual')"
      >
        Visual
      </button>
      <button
        class="px-3 py-1.5 text-xs font-medium"
        :class="
          activeTab === 'html'
            ? 'text-n-brand border-b-2 border-n-brand'
            : 'text-n-slate-10 hover:text-n-slate-12'
        "
        @click="switchTab('html')"
      >
        HTML
      </button>
    </div>

    <!-- Toolbar (visual mode) -->
    <div
      v-if="activeTab === 'visual'"
      class="flex flex-wrap items-center gap-0.5 px-2 py-1.5 border-b border-n-strong bg-n-surface-2"
    >
      <!-- Text format -->
      <button
        class="toolbar-btn"
        title="Negrito"
        @click="execCmd('bold')"
      >
        <span class="font-bold">B</span>
      </button>
      <button
        class="toolbar-btn"
        title="Itlico"
        @click="execCmd('italic')"
      >
        <span class="italic">I</span>
      </button>
      <button
        class="toolbar-btn"
        title="Sublinhado"
        @click="execCmd('underline')"
      >
        <span class="underline">U</span>
      </button>
      <button
        class="toolbar-btn"
        title="Riscado"
        @click="execCmd('strikeThrough')"
      >
        <span class="line-through">S</span>
      </button>

      <span class="w-px h-5 bg-n-strong mx-1" />

      <!-- Headings -->
      <select
        class="toolbar-select"
        @change="formatBlock($event.target.value); $event.target.value = ''"
      >
        <option value="">Formato</option>
        <option value="h1">Titulo 1</option>
        <option value="h2">Titulo 2</option>
        <option value="h3">Titulo 3</option>
        <option value="p">Paragrafo</option>
      </select>

      <span class="w-px h-5 bg-n-strong mx-1" />

      <!-- Alignment -->
      <button
        class="toolbar-btn"
        title="Alinhar esquerda"
        @click="execCmd('justifyLeft')"
      >
        <div class="i-lucide-align-left w-3.5 h-3.5" />
      </button>
      <button
        class="toolbar-btn"
        title="Centralizar"
        @click="execCmd('justifyCenter')"
      >
        <div class="i-lucide-align-center w-3.5 h-3.5" />
      </button>
      <button
        class="toolbar-btn"
        title="Alinhar direita"
        @click="execCmd('justifyRight')"
      >
        <div class="i-lucide-align-right w-3.5 h-3.5" />
      </button>

      <span class="w-px h-5 bg-n-strong mx-1" />

      <!-- Lists -->
      <button
        class="toolbar-btn"
        title="Lista ordenada"
        @click="execCmd('insertOrderedList')"
      >
        <div class="i-lucide-list-ordered w-3.5 h-3.5" />
      </button>
      <button
        class="toolbar-btn"
        title="Lista"
        @click="execCmd('insertUnorderedList')"
      >
        <div class="i-lucide-list w-3.5 h-3.5" />
      </button>

      <span class="w-px h-5 bg-n-strong mx-1" />

      <!-- Colors -->
      <label class="toolbar-btn relative" title="Cor do texto">
        <span class="text-xs font-bold">A</span>
        <input
          type="color"
          class="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
          value="#000000"
          @input="setColor"
        />
      </label>
      <label class="toolbar-btn relative" title="Cor de fundo">
        <div class="w-3.5 h-3.5 rounded bg-yellow-300 border border-n-strong" />
        <input
          type="color"
          class="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
          value="#ffff00"
          @input="setBgColor"
        />
      </label>

      <span class="w-px h-5 bg-n-strong mx-1" />

      <!-- Link & Image -->
      <button
        class="toolbar-btn"
        title="Inserir link"
        @click="insertLink"
      >
        <div class="i-lucide-link w-3.5 h-3.5" />
      </button>
      <button
        class="toolbar-btn"
        title="Inserir imagem"
        @click="insertImage"
      >
        <div class="i-lucide-image w-3.5 h-3.5" />
      </button>

      <span class="w-px h-5 bg-n-strong mx-1" />

      <!-- Undo/Redo -->
      <button
        class="toolbar-btn"
        title="Desfazer"
        @click="execCmd('undo')"
      >
        <div class="i-lucide-undo-2 w-3.5 h-3.5" />
      </button>
      <button
        class="toolbar-btn"
        title="Refazer"
        @click="execCmd('redo')"
      >
        <div class="i-lucide-redo-2 w-3.5 h-3.5" />
      </button>

      <span class="w-px h-5 bg-n-strong mx-1" />

      <!-- Variables -->
      <select
        class="toolbar-select"
        @change="insertVariable($event.target.value); $event.target.value = ''"
      >
        <option value="">Variavel</option>
        <option value="cumprimento">cumprimento</option>
        <option value="nome">nome</option>
        <option value="name">name</option>
        <option value="email">email</option>
        <option value="phone">phone</option>
        <option value="empresa">empresa</option>
      </select>

      <button
        class="toolbar-btn"
        title="Limpar formatacao"
        @click="execCmd('removeFormat')"
      >
        <div class="i-lucide-eraser w-3.5 h-3.5" />
      </button>
    </div>

    <!-- Visual Editor -->
    <div
      v-show="activeTab === 'visual'"
      ref="editorRef"
      contenteditable="true"
      class="min-h-[200px] max-h-[400px] overflow-y-auto px-4 py-3 text-sm text-n-slate-12 bg-n-surface-1 outline-none prose prose-sm max-w-none"
      @input="updateFromEditor"
    />

    <!-- HTML Source -->
    <textarea
      v-show="activeTab === 'html'"
      v-model="htmlSource"
      rows="12"
      class="w-full px-4 py-3 text-sm border-0 bg-n-surface-1 text-n-slate-12 font-mono resize-y outline-none min-h-[200px]"
      placeholder="<h1>Ola, {{nome}}!</h1><p>Conteudo do e-mail...</p>"
      @input="updateFromSource"
    />
  </div>
</template>

<style scoped>
.toolbar-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 4px;
  cursor: pointer;
  color: var(--n-slate-11);
  border: none;
  background: transparent;
}
.toolbar-btn:hover {
  background: var(--n-alpha-3, rgba(0, 0, 0, 0.08));
  color: var(--n-slate-12);
}
.toolbar-select {
  height: 28px;
  padding: 0 4px;
  font-size: 11px;
  border-radius: 4px;
  border: 1px solid var(--n-strong, #ddd);
  background: transparent;
  color: var(--n-slate-11);
  cursor: pointer;
}
.toolbar-select:hover {
  background: var(--n-alpha-3, rgba(0, 0, 0, 0.08));
}
</style>
